import { css } from "styled-components";

/**
 * Button variant styles – theme-driven, no hardcoded colors.
 * Use with theme from design-system/theme (semanticTheme). Keeps styling logic separate from component behavior.
 */
export function getVariantStyles(theme, variant = "primary") {
  const t = theme;
  const variants = {
    primary: css`
      background: ${t.colors.accent};
      color: #fff;
      border: 1px solid ${t.colors.accent};
      &:hover:not(:disabled) {
        background: ${t.colors.purpleLight};
        border-color: ${t.colors.purpleLight};
      }
      &:active:not(:disabled) {
        opacity: 0.95;
      }
    `,
    secondary: css`
      background: ${t.colors.backgroundAlt};
      color: ${t.colors.text};
      border: 1px solid ${t.colors.border};
      &:hover:not(:disabled) {
        background: ${t.colors.border};
        border-color: ${t.colors.textMuted};
      }
      &:active:not(:disabled) {
        background: ${t.colors.border};
      }
    `,
    outline: css`
      background: transparent;
      color: ${t.colors.accent};
      border: 1px solid ${t.colors.accent};
      &:hover:not(:disabled) {
        background: ${t.colors.accentLight};
      }
      &:active:not(:disabled) {
        background: ${t.colors.accentLight};
      }
    `,
    ghost: css`
      background: transparent;
      color: ${t.colors.text};
      border: 1px solid transparent;
      &:hover:not(:disabled) {
        background: ${t.colors.backgroundAlt};
      }
      &:active:not(:disabled) {
        background: ${t.colors.border};
      }
    `,
    link: css`
      background: transparent;
      color: ${t.colors.accent};
      border: none;
      text-decoration: underline;
      &:hover:not(:disabled) {
        color: ${t.colors.purpleLight};
      }
      &:active:not(:disabled) {
        opacity: 0.9;
      }
    `,
    danger: css`
      background: ${t.colors.error};
      color: #fff;
      border: 1px solid ${t.colors.error};
      &:hover:not(:disabled) {
        opacity: 0.9;
        filter: brightness(1.05);
      }
      &:active:not(:disabled) {
        opacity: 0.95;
      }
    `,
  };
  return variants[variant] ?? variants.primary;
}

/**
 * Button size styles – padding and font size from theme.
 */
export function getSizeStyles(theme, size = "md") {
  const t = theme;
  const sizes = {
    xs: css`
      padding: ${t.spacing.xs} ${t.spacing.sm};
      font-size: ${t.typography.fontSize.xs};
      min-height: 24px;
    `,
    sm: css`
      padding: ${t.spacing.sm} ${t.spacing.md};
      font-size: ${t.typography.fontSize.sm};
      min-height: 32px;
    `,
    md: css`
      padding: ${t.spacing.sm} ${t.spacing.lg};
      font-size: ${t.typography.fontSize.base};
      min-height: 40px;
    `,
    lg: css`
      padding: ${t.spacing.md} ${t.spacing.xl};
      font-size: ${t.typography.fontSize.lg};
      min-height: 48px;
    `,
    xl: css`
      padding: ${t.spacing.lg} ${t.spacing.xxl};
      font-size: ${t.typography.fontSize.xl};
      min-height: 56px;
    `,
  };
  return sizes[size] ?? sizes.md;
}

/**
 * State and option modifiers – disabled, loading, fullWidth, rounded, elevated, animation.
 */
export function getStateStyles(theme, options = {}) {
  const { disabled, loading, fullWidth, rounded, elevated, animation = "none" } = options;
  const t = theme;
  return css`
    ${disabled || loading
      ? css`
          cursor: not-allowed;
          opacity: 0.7;
        `
      : css`
          cursor: pointer;
        `}
    ${fullWidth
      ? css`
          width: 100%;
          justify-content: center;
        `
      : ""}
    ${rounded
      ? css`
          border-radius: 9999px;
        `
      : css`
          border-radius: ${t.borderRadius.md};
        `}
    ${elevated
      ? css`
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
          &:hover:not(:disabled) {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          }
        `
      : ""}
    ${animation === "scale"
      ? css`
          transition: transform 0.15s ease;
          &:active:not(:disabled) {
            transform: scale(0.98);
          }
        `
      : ""}
    ${animation === "ripple"
      ? css`
          position: relative;
          overflow: hidden;
          transition: opacity 0.2s;
        `
      : ""}
  `;
}
